import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  addComment,
  changeIssue,
  getDiscussion,
  openIssue,
  type Discussion,
  type DiscussionSubject,
  type Issue,
} from "../../api/content/discussionsApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { isAllowed } from "../../api/access/decisions";
import { chosenProjectDecision, useProjects } from "../../projects/projectContext";
import { formatTimeAgo } from "../../shared/formatting/relativeTimes";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import MarkdownRenderer from "../../ui/content/MarkdownRenderer";
import { FORM_COMPACT_CONTROL_CLASS } from "../../ui/styles/formStyles";
import { useMemberNames } from "../threads/data/useMemberNames";
import Chip from "../../ui/feedback/Chip";
import OptionLabel from "../../ui/forms/OptionLabel";
import LoadStatus from "../../ui/feedback/LoadStatus";

interface DiscussionPanelProps {
  readonly projectId: string;
  readonly subject: DiscussionSubject;
  /** The review the comments are written in, if any; recorded with each comment. */
  readonly changeSetId?: string | null;
  /** A discussion already loaded by the page (a review loads all its subjects at once). */
  readonly preloaded?: Discussion | null;
  readonly onChanged?: () => void;
  /** The host's frame around the panel, such as its heading; hidden with it (W-09d). */
  readonly frame?: (panel: ReactNode) => ReactNode;
}

/**
 * Comments and issues on one subject: an item, a term, a change set, a Thread entry or a
 * version (FR-VERSION-6). Usable on any item page. Writing needs `comment` in the project.
 * Hidden, with the host's frame, from someone who can't comment while there is nothing to
 * read (P-B, W-09d); loading and errors still show.
 */
export default function DiscussionPanel({ projectId, subject, changeSetId = null, preloaded = null, onChanged, frame = (panel) => panel }: DiscussionPanelProps) {
  const { t } = useTranslation();
  const projects = useProjects();
  const { kind, value } = subject;
  // Keyed by the subject's parts, so a subject written inline does not reload on every render.
  const load = useCallback(
    (signal: AbortSignal) =>
      preloaded !== null ? Promise.resolve(preloaded) : getDiscussion(projectId, { kind, value }, signal),
    [projectId, kind, value, preloaded],
  );
  const { state, refresh } = useLoad(load);
  const changed = () => {
    refresh();
    onChanged?.();
  };

  const shown = state.status === "loaded" ? filtered(state.value, subject) : null;
  if (shown !== null && shown.comments.length === 0 && shown.issues.length === 0 && !isAllowed(chosenProjectDecision(projects, "comment"))) return null;
  return frame(
    <section className="space-y-2" aria-label={t("discussion.title")}>
      <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={refresh} compact />
      {shown === null ? null : (
        <DiscussionEntries
          projectId={projectId}
          subject={subject}
          changeSetId={changeSetId}
          discussion={shown}
          onChanged={changed}
        />
      )}
      <CommentForm projectId={projectId} subject={subject} changeSetId={changeSetId} onChanged={changed} />
    </section>,
  );
}

function filtered(discussion: Discussion, subject: DiscussionSubject): Discussion {
  const same = (other: DiscussionSubject) => other.kind === subject.kind && other.value === subject.value;
  return {
    comments: discussion.comments.filter((comment) => same(comment.subject)),
    issues: discussion.issues.filter((issue) => same(issue.subject)),
    names: discussion.names,
  };
}

function DiscussionEntries({
  projectId,
  subject,
  changeSetId,
  discussion,
  onChanged,
}: {
  projectId: string;
  subject: DiscussionSubject;
  changeSetId: string | null;
  discussion: Discussion;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const nameOf = useDiscussionNames(projectId, discussion);
  const loose = discussion.comments.filter((comment) => comment.issueId === null);
  if (discussion.comments.length === 0 && discussion.issues.length === 0) {
    return <p className="app-text-muted text-xs">{t("discussion.empty")}</p>;
  }
  return (
    <div className="space-y-2">
      {discussion.issues.map((issue) => (
        <IssueBlock
          key={issue.issueId}
          issue={issue}
          context={{ projectId, subject, changeSetId }}
          discussion={discussion}
          onChanged={onChanged}
        />
      ))}
      <ul className="space-y-1.5">
        {loose.map((comment) => (
          <li key={comment.commentId} className="app-surface rounded border px-2 py-1.5">
            <p className="app-text-muted text-xs">
              {nameOf(comment.author)} · {formatTimeAgo(comment.at, i18n.resolvedLanguage)}
            </p>
            <MarkdownRenderer content={comment.body} />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface DiscussionContext {
  readonly projectId: string;
  readonly subject: DiscussionSubject;
  readonly changeSetId: string | null;
}

function IssueBlock({
  issue,
  context,
  discussion,
  onChanged,
}: {
  issue: Issue;
  context: DiscussionContext;
  discussion: Discussion;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { projectId, subject, changeSetId } = context;
  const nameOf = useDiscussionNames(projectId, discussion);
  const submission = useSubmission();
  const projects = useProjects();
  const comments = discussion.comments.filter((comment) => comment.issueId === issue.issueId);
  const event = issue.state === "open" ? "resolve" : "reopen";
  return (
    <div className="app-card space-y-1 p-2">
      <p className="flex items-center gap-2 text-sm">
        <Chip>{t(`discussion.issueStates.${issue.state}`)}</Chip>
        <span className="app-title flex-1 font-semibold">{issue.title}</span>
        {isAllowed(chosenProjectDecision(projects, "comment")) ? (
          <AppButton
            size="xs"
            variant="ghost"
            disabled={submission.state.status === "submitting"}
            onClick={() => {
              submission.submit(async () => {
                await changeIssue(issue, { event, reason: t(`discussion.issueReasons.${event}`) });
                onChanged();
              });
            }}
          >
            {t(`discussion.issueActions.${event}`)}
          </AppButton>
        ) : null}
      </p>
      <p className="app-text-muted text-xs">{t("discussion.openedBy", { name: nameOf(issue.openedBy) })}</p>
      {comments.map((comment) => (
        <div key={comment.commentId} className="border-l-2 pl-2 text-sm">
          <MarkdownRenderer content={comment.body} />
        </div>
      ))}
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} compact={true} /> : null}
      <CommentForm projectId={projectId} subject={subject} changeSetId={changeSetId} issueId={issue.issueId} onChanged={onChanged} />
    </div>
  );
}

function CommentForm({
  projectId,
  subject,
  changeSetId,
  issueId = null,
  onChanged,
}: {
  projectId: string;
  subject: DiscussionSubject;
  changeSetId: string | null;
  issueId?: string | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const projects = useProjects();
  const submission = useSubmission();
  const [text, setText] = useState("");
  const [asIssue, setAsIssue] = useState(false);
  if (!isAllowed(chosenProjectDecision(projects, "comment"))) {
    return null;
  }
  const send = () => {
    const body = text.trim();
    if (body === "") {
      return;
    }
    submission.submit(async () => {
      if (asIssue && issueId === null) {
        const [title = body, ...rest] = body.split("\n");
        await openIssue(projectId, { subject, title: title.slice(0, 200), body: rest.join("\n").trim(), changeSetId });
      } else {
        await addComment(projectId, { subject, body, issueId, changeSetId });
      }
      setText("");
      setAsIssue(false);
      onChanged();
    });
  };
  return (
    <div className="space-y-1">
      <textarea
        className={FORM_COMPACT_CONTROL_CLASS}
        rows={2}
        value={text}
        placeholder={t(issueId === null ? "discussion.placeholder" : "discussion.replyPlaceholder")}
        aria-label={t(issueId === null ? "discussion.placeholder" : "discussion.replyPlaceholder")}
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      <div className="flex items-center gap-2">
        {issueId === null ? (
          <OptionLabel size="small" className="app-text-muted gap-1">
            <input
              type="checkbox"
              checked={asIssue}
              onChange={(event) => {
                setAsIssue(event.target.checked);
              }}
            />
            {t("discussion.asIssue")}
          </OptionLabel>
        ) : null}
        <span className="flex-1" />
        <AppButton size="xs" variant="primary" disabled={text.trim() === "" || submission.state.status === "submitting"} onClick={send}>
          {t(asIssue ? "discussion.openIssue" : "discussion.send")}
        </AppButton>
      </div>
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} compact={true} /> : null}
    </div>
  );
}

/**
 * A person's name in a discussion: "you" for the signed-in user, else the name the server
 * read with the discussion, which covers non-members such as a problem's reporter (W-08c),
 * else the project's members.
 */
function useDiscussionNames(projectId: string, discussion: Discussion): (userId: string) => string {
  const people = useMemberNames(projectId);
  return (userId) => (userId === people.currentUserId ? people.nameOf(userId) : (discussion.names.get(userId) ?? people.nameOf(userId)));
}
